import { generateFinanceLink } from 'components/PageWrappers/AdDetails/helpers';
import { fireToast } from 'helpers/Toasts';
import { accountsApi } from '../../../../../api/accountsApi';
import {
  SELLER_ANALYTICS_CATEGORIES,
  SELLER_ANALYTICS_TIME_RANGES,
} from 'types';
import { formatAuthorizationHeader } from 'helpers/auth';
import { pushToDataLayer } from 'services/gtmService';
import { handleCallStripeCheckout } from 'helpers/handleCallStripeCheckout';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { useAdDetailsContext } from 'contexts/AdDetailsContext';
import { useUserContext } from 'contexts/UserContext';
import type { useAdTitleBoxProps } from 'components/PageWrappers/AdDetails/components/AdTitleBox/AdTitleBox.typed';
import { rg4js } from 'helpers/raygun';
import { PAGE } from 'helpers/pages';
import { NO_PRICE_TEXT, PRICE_ON_REQUEST_TEXT } from 'helpers/constants';

const useAdTitleBox = (props: useAdTitleBoxProps) => {
  const {
    adId,
    carFinance,
    currency,
    financeInfo,
    financeLink,
    price,
    priceOnRequest,
    title,
  } = props;

  const { accessToken } = useAuthOptions();
  const { setIsStripeModalOpen, stripeQuantity, setIsPricePerMonthModalOpen } =
    useAdDetailsContext();
  const {
    user,
    setIsSellerAnalyticsModalOpen,
    setSellerAnalyticsModalDisplay,
  } = useUserContext();

  // We're going to keep the logic for stripe, even though we're removing it from the possibly rendering in the ui.
  // This will be revisited in the future.

  async function handleCheckout() {
    if (!user?.verification.phone) {
      setIsStripeModalOpen(true);
    } else {
      handleCallStripeCheckout(adId, stripeQuantity);
    }
  }

  async function handleAnalyticsView(userId: number) {
    try {
      const { data } = await accountsApi.getSellerAnalyticsData(
        userId,
        adId,
        SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
        SELLER_ANALYTICS_TIME_RANGES.SEVEN_DAYS,
        formatAuthorizationHeader(accessToken),
      );
      setIsSellerAnalyticsModalOpen(true);
      setSellerAnalyticsModalDisplay({
        title,
        selectedTab: SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
        data: data.plotValues,
      });
      pushToDataLayer({
        event: 'seller_analytics',
        event_category: 'ad_management',
        event_action: 'view_ad_analytics',
        event_label: 'ad details page',
      });
    } catch (error) {
      rg4js('send', {
        error,
        tags: [PAGE.AD_DETAILS],
        customData: { functionName: 'handleAnalyticsView' },
      });
      fireToast({
        type: 'ERROR',
        text: 'Oops, something went wrong! Please try again later',
      });
    }
  }

  const hasFinanceLink = Boolean(financeLink || financeInfo);
  const finance = {
    text: financeLink?.title || 'Get Finance Approval',
    url:
      financeLink?.url ||
      (financeInfo && generateFinanceLink(financeInfo)) ||
      '',
  };

  function handleFinanceLinkClick() {
    pushToDataLayer({
      event: 'partner_clicks',
      servicePartnerType: carFinance?.banner?.partnerType,
      servicePartnerName: carFinance?.banner?.partnerName,
    });
  }

  const financeClassImp = `${carFinance?.banner?.partnerName}-${carFinance?.banner?.partnerType}-imp`;

  const priceText = () => {
    if (priceOnRequest == true) {
      return PRICE_ON_REQUEST_TEXT;
    }
    if (!price) return NO_PRICE_TEXT;
    if (!currency) return price;
    const selectedCurrency = currency === 'EUR' ? '€' : '£';
    return `${selectedCurrency}${price}`;
  };

  const noPriceOrUnrealisticPrice = Boolean(
    priceOnRequest || price === '0' || !price,
  );

  const handlePPMClick = () => {
    setIsPricePerMonthModalOpen(true);
  };

  return {
    handleCheckout,
    handleAnalyticsView,
    hasFinanceLink,
    finance,
    handleFinanceLinkClick,
    financeClassImp,
    priceText,
    noPriceOrUnrealisticPrice,
    handlePPMClick,
  };
};

export { useAdTitleBox };
